import {
  Text,
  Menu,
  Badge,
  Stack,
  Group,
  Button,
  Grid,
  ActionIcon,
  UnstyledButton,
  ThemeIcon,
} from "@mantine/core";
import {
  IconAdjustments,
  IconCircleCheck,
  IconCopy,
  IconPlus,
  IconSettings,
} from "@tabler/icons";
import moment from "moment";
import { useEffect, useState } from "react";
import _ from "lodash";
import { useAuthUser } from "react-auth-kit";
import { useTranslation } from "react-i18next";
import { useServerApi } from "../hooks/userServerApi";
import { CommissionScheduleProductCard } from "./commissionScheduleProductList";
import ProductCommissionScheduleEditor from "./productCommissionScheduleEditor";

const VersionViewer = ({
  rateFactor = 1,
  scheduleProductId,
  version,
  name,
  showGrossRateOnly = false,
}) => {
  return (
    <div>
      <CommissionScheduleProductCard
        scheduleProductId={scheduleProductId}
        version={version}
        showCode={false}
        showProductInfo={false}
        name={name}
        showGrossRateOnly={showGrossRateOnly}
        rateFactor={rateFactor}
      />
    </div>
  );
};

const ProductCommissionScheduleTable = ({
  form,
  name,
  disabled,
  rateFactor = 1, //0.525
  firstYearMaxPercentage, // for first year rate limit
  version = "COMPANY", //"CONSULTANT"
  showGrossRateOnly = false,
  ...props
}) => {
  const { t: translate } = useTranslation();
  const [api] = useServerApi();
  const id = form.values._id;
  const [scheduleProductList, setScheduleProductList] = useState([]);
  const [selected, setSelected] = useState(0);
  const auth = useAuthUser();

  const isSelected = (index) => index === selected;
  const isCurrentTable = (scheduleProduct) => {
    if (!scheduleProduct?.effectiveDate) return false;
    const now = new Date();
    const effective = new Date(scheduleProduct.effectiveDate);
    const end = scheduleProduct.endDate ? new Date(scheduleProduct.endDate) : null;
    if (Number.isNaN(effective.getTime())) return false;
    if (now < effective) return false;
    if (end && !Number.isNaN(end.getTime()) && now > end) return false;
    return true;
  };
  const canEdit = version === "COMPANY" && !disabled && !!id;
  const [editor, setEditor] = useState(null);
  const [refreshToken, setRefreshToken] = useState(0);

  const VersionSelector = () => (
    <>
      {scheduleProductList.map((scheduleProduct, index) => (
        <UnstyledButton
          key={index}
          style={{ width: "100%" }}
          onClick={() => {
            setSelected(index);
          }}
        >
          <div
            key={index}
            style={{
              border: "1px solid",
              borderColor: isSelected(index) ? "lightgray" : "gray",
              padding: "10px",
              marginBottom: "20px",
              borderRadius: 10,
            }}
          >
            <Group position="apart">
              <Group spacing={6}>
                <Text size="sm" weight={800}>
                  {"V" + (scheduleProductList.length - index)}
                </Text>
                <Badge
                  size="xs"
                  radius={0}
                  color={scheduleProduct.professionalInvestor ? "orange" : "gray"}
                >
                  {scheduleProduct.professionalInvestor ? "PI" : "Non-PI"}
                </Badge>
              </Group>

              {isCurrentTable(scheduleProduct) && (
                <ThemeIcon
                  color="teal"
                  size={20}
                  radius="xl"
                  aria-label="Current commission version"
                >
                  <IconCircleCheck size={18} />
                </ThemeIcon>
              )}
            </Group>

            {/* <Group position="apart"> */}
            <Text color="dimmed" size="xs">
              {translate("Start : ")}
              {scheduleProduct.effectiveDate
                ? moment(scheduleProduct.effectiveDate).format("YYYY-MM-DD")
                : " ---"}
            </Text>

            <Text color="dimmed" size="xs">
              {translate("End : ")}
              {scheduleProduct.endDate
                ? moment(scheduleProduct.endDate).format("YYYY-MM-DD")
                : " ---"}
            </Text>
            {scheduleProduct.commissionSchedule?.code && (
              <Text color="dimmed" size={10} mt={4}>
                {scheduleProduct.commissionSchedule.code} &gt; {scheduleProduct.code}
              </Text>
            )}
          </div>
        </UnstyledButton>
      ))}
    </>
  );

  const fetchScheduleProductList = async () => {
    try {
      if (!id) return;
      const result = await api.Product.getCommissionScheduleProductList(id);
      //   console.log(result);
      setScheduleProductList(result);
    } catch (error) {
      console.log(error);
    }
  };

  useEffect(() => {
    fetchScheduleProductList();
  }, [id]);

  const [actualRateFactor, setActualRateFactor] = useState(0);

  const setActualRateByConsultant = async () => {
    if (!auth().id) return;

    //get producter rate by consultant
    const result = await api.User.getProducerRate(auth().id);
    if (!result || !result.rate) {
      setActualRateFactor(0);
      return;
    }
    setActualRateFactor(result.rate);
  };

  useEffect(() => {
    if (version === "COMPANY") {
      setActualRateFactor(1);
    } else {
      setActualRateByConsultant();
    }
  }, [version, auth().id]);

  const selectedScheduleProduct = scheduleProductList[selected];

  const versionName = selectedScheduleProduct
    ? "V" + (scheduleProductList.length - selected)
    : "";

  return (
    <div>
      {_.isEmpty(scheduleProductList) && (
        <Stack spacing="sm">
          <Text size="sm" color="dimmed">
            No Commission Schedule Data
          </Text>
          {canEdit && (
            <div>
              <Button
                leftIcon={<IconPlus size={16} />}
                onClick={() => setEditor({ mode: "create", scheduleProductId: null })}
              >
                Add Commission
              </Button>
            </div>
          )}
        </Stack>
      )}
      {!_.isEmpty(scheduleProductList) && (
        <Stack spacing="sm">
          {canEdit && (
            <Group>
              <Menu shadow="md" width={200}>
                <Menu.Target>
                  <ActionIcon variant="default" aria-label="Commission actions">
                    <IconSettings size={20} />
                  </ActionIcon>
                </Menu.Target>
                <Menu.Dropdown>
                  <Menu.Label>Action</Menu.Label>
                  <Menu.Item
                    icon={<IconAdjustments size={14} />}
                    onClick={() =>
                      setEditor({
                        mode: "edit",
                        scheduleProductId: selectedScheduleProduct?._id,
                      })
                    }
                  >
                    Edit
                  </Menu.Item>
                  <Menu.Item
                    icon={<IconCopy size={14} />}
                    onClick={() =>
                      setEditor({
                        mode: "create",
                        scheduleProductId: selectedScheduleProduct?._id,
                      })
                    }
                  >
                    Copy to New
                  </Menu.Item>
                </Menu.Dropdown>
              </Menu>
            </Group>
          )}
          <Grid grow gutter="xl">
            <Grid.Col span={10}>
              <VersionViewer
                key={`${selectedScheduleProduct?._id || "none"}-${refreshToken}`}
                rateFactor={actualRateFactor}
                scheduleProductId={selectedScheduleProduct?._id}
                version={version}
                name={versionName}
                showGrossRateOnly={showGrossRateOnly}
              />
            </Grid.Col>

            <Grid.Col span={2}>
              <VersionSelector scheduleProductList={scheduleProductList} />
            </Grid.Col>
          </Grid>
        </Stack>
      )}
      {editor && canEdit && (
        <ProductCommissionScheduleEditor
          key={`${editor.mode}-${editor.scheduleProductId || "new"}`}
          mode={editor.mode}
          productId={id}
          scheduleProductId={editor.scheduleProductId}
          versionName={editor.mode === "edit" ? versionName : ""}
          onClose={() => setEditor(null)}
          onSaved={(savedMode) => {
            setEditor(null);
            setRefreshToken((token) => token + 1);
            if (savedMode === "create") setSelected(0);
            fetchScheduleProductList();
          }}
        />
      )}
    </div>
  );
};

export default ProductCommissionScheduleTable;
